import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import { LOCATION_TREE } from '../data/locations';
import useServiceCategories from '../data/useServiceCategories';
import './Freelancers.css';

const RATE_RANGES = [
  { key: 'all', label: '不限价格' },
  { key: '0-50', label: '¥0-50/小时' },
  { key: '50-80', label: '¥50-80/小时' },
  { key: '80+', label: '¥80+/小时' },
];
const SORT_OPTIONS = [
  { key: 'rating', label: '评分最高' },
  { key: 'reviews', label: '评价最多' },
  { key: 'rate_asc', label: '价格从低到高' },
  { key: 'rate_desc', label: '价格从高到低' },
];

function defaultAvatarFor(nameOrId) {
  const initial = String(nameOrId || 'U').trim().slice(0, 1).toUpperCase() || 'U';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" rx="48" fill="#108a5f"/><text x="48" y="58" text-anchor="middle" font-family="Arial, sans-serif" font-size="38" font-weight="700" fill="#ffffff">${initial}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function normalizeFreelancer(item = {}) {
  const name = String(item.name || item.username || '服务者').trim();
  return {
    ...item,
    id: item.id,
    name,
    avatar: item.avatar || defaultAvatarFor(name || item.id),
    title: item.title || '平台服务者',
    category: item.category || '',
    skills: Array.isArray(item.skills) ? item.skills.map(String).filter(Boolean) : [],
    rating: Number(item.rating || 0),
    reviews: Number(item.reviews || 0),
    rate: Number(item.rate || 0),
    jobs: Number(item.jobs || 0),
    country: item.country || '',
    city: item.city || '',
    district: item.district || '',
    online: item.online !== false,
  };
}

const FL_TEXT = {
  zh: {
    title: '寻找自由职业者',
    subtitle: '汇聚优秀技能人才，为你的项目找到合适伙伴',
    searchPlaceholder: '搜索名字、职业或技能...',
    category: '分类',
    hourly: '时薪',
    location: '位置',
    count: n => `找到 ${n} 位自由职业者`,
    empty: '暂无符合条件的技术员。请先让技术员发布真实服务，审核通过后会显示在这里。',
    loading: '正在更新服务者列表...',
    jobs: n => `${n} 单`,
    perHour: '小时',
    categories: {
      全部: '全部',
    },
    rates: {
      不限价格: '不限价格',
      '¥0-50/小时': '¥0-50/小时',
      '¥50-80/小时': '¥50-80/小时',
      '¥80+/小时': '¥80+/小时',
    },
    sorts: {
      评分最高: '评分最高',
      评价最多: '评价最多',
      价格从低到高: '价格从低到高',
      价格从高到低: '价格从高到低',
    },
    phrases: {
      全栈开发工程师: '全栈开发工程师',
      移动端开发者: '移动端开发者',
      数据科学家: '数据科学家',
      内容写作者: '内容写作者',
      营销专家: '营销专家',
      前端开发: '前端开发',
      'QA 测试工程师': 'QA 测试工程师',
      视频剪辑师: '视频剪辑师',
      翻译专家: '翻译专家',
      'Logo 设计师': 'Logo 设计师',
      技术写作: '技术写作',
      文案: '文案',
      自动化测试: '自动化测试',
      字幕: '字幕',
      中英互译: '中英互译',
      日语: '日语',
      本地化: '本地化',
      品牌: '品牌',
    },
  },
  en: {
    title: 'Find freelancers',
    subtitle: 'Find skilled experts for your project without extra complexity.',
    searchPlaceholder: 'Search name, role, or skill...',
    category: 'Category',
    hourly: 'Hourly rate',
    location: 'Location',
    count: n => `${n} freelancers found`,
    empty: 'No matching providers yet. Approved real service providers will appear here.',
    loading: 'Updating provider results...',
    jobs: n => `${n} completed services`,
    perHour: 'hr',
    categories: {
      全部: 'All',
    },
    rates: {
      不限价格: 'Any rate',
      '¥0-50/小时': '¥0-50/hr',
      '¥50-80/小时': '¥50-80/hr',
      '¥80+/小时': '¥80+/hr',
    },
    sorts: {
      评分最高: 'Highest rated',
      评价最多: 'Most reviewed',
      价格从低到高: 'Lowest rate',
      价格从高到低: 'Highest rate',
    },
    phrases: {
      全栈开发工程师: 'Full-stack developer',
      'UI/UX 设计师': 'UI/UX Designer',
      移动端开发者: 'Mobile developer',
      数据科学家: 'Data scientist',
      'DevOps 工程师': 'DevOps engineer',
      内容写作者: 'Content writer',
      营销专家: 'Marketing specialist',
      前端开发: 'Frontend developer',
      'QA 测试工程师': 'QA engineer',
      视频剪辑师: 'Video editor',
      翻译专家: 'Translation specialist',
      'Logo 设计师': 'Logo designer',
      技术写作: 'Technical writing',
      文案: 'Copywriting',
      自动化测试: 'Test automation',
      字幕: 'Subtitles',
      中英互译: 'Chinese-English translation',
      日语: 'Japanese',
      本地化: 'Localization',
      品牌: 'Branding',
    },
  },
};

export default function Freelancers() {
  const { language, localizeCategory } = useLanguage();
  const serviceCategories = useServiceCategories();
  const [searchParams] = useSearchParams();
  const text = FL_TEXT[language] || FL_TEXT.en;
  const [category, setCategory] = useState(searchParams.get('category') || '全部');
  const [rate, setRate] = useState('all');
  const [sort, setSort] = useState(searchParams.get('sort') || 'rating');
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');
  const [locationParts, setLocationParts] = useState(['', '', '']);
  const [liveFreelancers, setLiveFreelancers] = useState([]);
  const [freelancersLoading, setFreelancersLoading] = useState(true);

  const selectedCountry = LOCATION_TREE.find(item => item.value === locationParts[0]);
  const selectedCity = selectedCountry?.cities.find(item => item.value === locationParts[1]);
  const categories = useMemo(
    () => ['全部', ...serviceCategories.map(item => item.name)],
    [serviceCategories],
  );

  useEffect(() => {
    const nextCategory = searchParams.get('category') || '全部';
    const nextSort = searchParams.get('sort') || 'rating';
    setCategory(categories.includes(nextCategory) ? nextCategory : '全部');
    setSort(['rating', 'reviews', 'rate_asc', 'rate_desc'].includes(nextSort) ? nextSort : 'rating');
  }, [categories, searchParams]);

  const updateLocation = (index, value) => {
    setLocationParts(current => {
      const next = [...current];
      next[index] = value;
      if (index === 0) {
        next[1] = '';
        next[2] = '';
      } else if (index === 1) {
        next[2] = '';
      }
      setLocation(next.filter(Boolean).join(':'));
      return next;
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setFreelancersLoading(true);
    setLiveFreelancers([]);

    api.get('/marketplace/freelancers', {
      params: {
        keyword,
        category: category === '全部' ? 'all' : category,
        location,
        sort,
        page: 1,
        limit: 24,
      },
      signal: controller.signal,
    })
      .then(res => {
        if (!active) return;
        const freelancers = Array.isArray(res.data.freelancers) ? res.data.freelancers.map(normalizeFreelancer) : [];
        setLiveFreelancers(freelancers);
      })
      .catch(err => {
        if (!active || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setLiveFreelancers([]);
      })
      .finally(() => {
        if (active) setFreelancersLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [keyword, category, sort, location]);

  const filtered = useMemo(() => {
    let list = liveFreelancers.slice();
    if (category !== '全部') list = list.filter(f => f.category === category);
    if (rate === '0-50') list = list.filter(f => f.rate <= 50);
    else if (rate === '50-80') list = list.filter(f => f.rate > 50 && f.rate <= 80);
    else if (rate === '80+') list = list.filter(f => f.rate > 80);
    if (keyword.trim()) {
      const q = keyword.trim().toLowerCase();
      list = list.filter(f =>
        f.name.toLowerCase().includes(q) ||
        f.title.toLowerCase().includes(q) ||
        f.skills.some(s => s.toLowerCase().includes(q))
      );
    }
    if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    else if (sort === 'reviews') list.sort((a, b) => b.reviews - a.reviews);
    else if (sort === 'rate_asc') list.sort((a, b) => a.rate - b.rate);
    else if (sort === 'rate_desc') list.sort((a, b) => b.rate - a.rate);
    return list;
  }, [liveFreelancers, category, rate, sort, keyword]);

  const phrase = value => text.phrases[value] || value;

  return (
    <div className="freelancers-page">
      <div className="fl-header">
        <div className="container">
          <h1>{text.title}</h1>
          <p>{text.subtitle}</p>
          <div className="fl-search">
            <input
              type="text"
              placeholder={text.searchPlaceholder}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="container fl-content">
        <aside className="fl-sidebar">
          <div className="fl-filter-group">
            <h3>{text.category}</h3>
            <div className="fl-chips">
              {categories.map(c => (
                <button
                  key={c}
                  className={`fl-chip ${category === c ? 'active' : ''}`}
                  onClick={() => setCategory(c)}
                >{c === '全部' ? text.categories[c] : localizeCategory(c)}</button>
              ))}
            </div>
          </div>

          <div className="fl-filter-group">
            <h3>{text.location}</h3>
            <div className="fl-location-grid">
              <select value={locationParts[0]} onChange={e => updateLocation(0, e.target.value)} aria-label={text.location}>
                <option value="">{text.location}</option>
                {LOCATION_TREE.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
              </select>
              <select value={locationParts[1]} disabled={!selectedCountry} onChange={e => updateLocation(1, e.target.value)} aria-label={language === 'zh' ? '城市' : 'City'}>
                <option value="">{language === 'zh' ? '全部城市' : 'All cities'}</option>
                {selectedCountry?.cities.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
              </select>
              <select value={locationParts[2]} disabled={!selectedCity || !selectedCity.districts.length} onChange={e => updateLocation(2, e.target.value)} aria-label={language === 'zh' ? '区域' : 'District'}>
                <option value="">{language === 'zh' ? '全部区域' : 'All districts'}</option>
                {selectedCity?.districts.map(item => <option key={item.value} value={item.value}>{item.label[language] || item.value}</option>)}
              </select>
            </div>
          </div>

          <div className="fl-filter-group">
            <h3>{text.hourly}</h3>
            {RATE_RANGES.map(r => (
              <label key={r.key} className="fl-radio">
                <input type="radio" name="rate" checked={rate === r.key} onChange={() => setRate(r.key)} />
                {text.rates[r.label] || r.label}
              </label>
            ))}
          </div>
        </aside>

        <main className="fl-main">
          <div className="fl-toolbar">
            <div className="fl-count">{text.count(filtered.length)}</div>
            <select className="fl-sort" value={sort} onChange={e => setSort(e.target.value)}>
              {SORT_OPTIONS.map(o => <option key={o.key} value={o.key}>{text.sorts[o.label] || o.label}</option>)}
            </select>
          </div>

          {freelancersLoading ? (
            <div className="fl-loading" aria-live="polite">{text.loading}</div>
          ) : filtered.length === 0 ? (
            <div className="fl-empty">{text.empty}</div>
          ) : (
            <div className="fl-grid">
              {filtered.map(f => (
                <Link key={f.id} to={`/freelancers/${f.id}`} className="fl-card">
                  <div className="fl-card-head">
                    <img src={f.avatar} alt={f.name} className="fl-avatar" />
                    {f.online && <span className="fl-online-dot" aria-label={language === 'zh' ? '在线' : 'Online'} />}
                    {f.verified && <span className="fl-badge-v">✓</span>}
                  </div>
                  <h3>{f.name}</h3>
                  <p className="fl-title">{phrase(f.title)}</p>
                  <div className="fl-rating">
                    <span className="stars">⭐ {f.rating}</span>
                    <span className="fl-reviews">({f.reviews})</span>
                  </div>
                  <div className="fl-skills">
                    {f.skills.slice(0, 3).map(s => (
                      <span key={s} className="fl-skill">{phrase(s)}</span>
                    ))}
                  </div>
                  <div className="fl-card-footer">
                    <span className="fl-rate">¥{f.rate}/{text.perHour}</span>
                    <span className="fl-jobs">{text.jobs(f.jobs)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
